<script setup lang="ts">
import { AppButton, AppFormCard, AppFormHeader } from '@/core/components';
import RequiredDocumentForm from './RequiredDocumentForm.vue';
import { DocumentType } from '@/core/enums/documentType';

const props = withDefaults(
  defineProps<{
    title?: string;
    description?: string;

    isOnboarding?: boolean;
  }>(),
  {
    title: 'NDIS Orientation Modules',
    description: '',

    isOnboarding: true,
  }
);

const emits = defineEmits<{
  (e: 'success'): void;
  (e: 'next', next: string): void;
  (e: 'skip', next: string): void;
}>();

const pending = ref(false);
const ndisModuleForm = ref();
const { showSnackbar } = useSnackbar();

const isDisabled = computed((): boolean => {
  if (!ndisModuleForm.value) return true;

  return ndisModuleForm.value.isDisabled || (props.isOnboarding ? false : !ndisModuleForm.value.hasChanges);
});

const submit = async () => {
  let success = false;

  if (ndisModuleForm.value) {
    success = await ndisModuleForm.value.handleSubmit();
  }

  if (success) {
    if (props.isOnboarding) {
      emits('next', 'required-documents');
    } else {
      showSnackbar({ text: 'NDIS Orientation Modules saved successfully', state: true });

      emits('success');
    }
  }
};

defineExpose({
  submit,
  pending,
  isDisabled,
});
</script>

<template>
  <div class="ndis">
    <AppFormHeader
      v-if="!isOnboarding"
      :title="title"
      :description="description"
      :loading="pending || isDisabled"
      class="mb-5"
      @submit="submit()"
    />

    <v-form @submit.prevent="submit">
      <AppFormCard
        title="NDIS Orientation Modules"
        subtitle="Indicate the date of issue for your NDIS Orientation Modules"
        class="mt-4"
        no-gutter
      >
        <RequiredDocumentForm
          ref="ndisModuleForm"
          :document-type="DocumentType.NDIS_ORIENTATION_MODULE"
          @pending="pending = $event"
          no-border
          hide-media
        />
      </AppFormCard>

      <AppButton
        v-if="isOnboarding"
        block
        color="primary"
        class="mt-4"
        rounded="xl"
        :disabled="isDisabled"
        :loading="pending"
      >
        Save
      </AppButton>
    </v-form>
  </div>
</template>

<style lang="scss" scoped>
.ndis {
  width: 100%;
  max-width: var(--form-max-width);
}

.inner-card {
  border: 1px solid #e0e0e0;
}
</style>
